import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
} from '../utils';

export const networkGraphOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color for the background. You may use a dataSource to apply the color. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    source: {
        default: '> primary | frameBySeriesTypes("string") | seriesByIndex(0)',
        description: 'Specify the array of node Ids that links start from.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    target: {
        default: '> primary | frameBySeriesTypes("string") | seriesByIndex(1)',
        description: 'Specify the array of node Ids that links go to.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeIds: {
        default: '> source',
        description:
            'Specify the array of node Ids to use for node styling. By default this is the same as the from array.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeTypes: {
        description:
            'Specify the node type for each node in the nodeIds array. Needed for clustering when layout is "force".',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeValues: {
        default: '> primary | frameBySeriesTypes("number") | seriesByIndex(0)',
        description: 'Specify the node value for each node in the nodeIds array.',
        type: 'array',
        items: {
            type: 'number',
        },
    },
    nodeTexts: {
        default: '> primary | frameBySeriesTypes("string") | seriesByIndex(0)',
        description: 'Specify the text to display under each node in the nodeIds array.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeColors: {
        description: 'Specify the color used for nodes. You may use a dataSource to apply color dynamically.',
        type: ['string', 'array'],
        items: {
            type: 'string',
            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        },
        default: '> themes.defaultNodeColor',
    },
    nodeSize: {
        description:
            'Specify a number to set the same diameter for all nodes or an array of numbers to set a custom diameter for each node. (8px - 200px)',
        type: ['number', 'array'],
        items: {
            type: 'number',
            minimum: 8,
            maximum: 200,
        },
        default: 48,
    },
    nodeColorValues: {
        description: 'Specify the array of values used for dynamic node coloring.',
        type: 'array',
        items: {
            type: ['string', 'number'],
        },
        default: '> primary | seriesByType("number")',
    },
    nodeIcons: {
        description: 'Specify the icon used for each node (cylinder, portrait, magnifier, etc.).',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeIconColors: {
        description:
            'Specify the color used for each node icon. You may use a dataSource to apply color dynamically.',
        type: ['array', 'string'],
        items: {
            type: 'string',
            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        },
        default: '> themes.defaultNodeIconColor',
    },
    nodeIconColorValues: {
        description: 'Specify the array of values used for dynamic node icon coloring.',
        type: 'array',
        items: {
            type: ['string', 'number'],
        },
        default: '> primary | seriesByType("number")',
    },
    linkStyle: {
        default: 'straight',
        description: 'Specify the link shape (s, z, c, straight).',
        type: 'string',
        pattern: getPattern(['s', 'z', 'c', 'straight']),
    },
    linkColors: {
        description: 'Specify the color used for links. You may use a dataSource to apply color dynamically.',
        type: ['array', 'string'],
        items: {
            type: 'string',
            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        },
        default: '> themes.defaultLinkColor',
    },
    linkWidth: {
        description:
            'Specify a number to set the same width of all links or an array of numbers to set a custom width for each link. Width is in pixels.',
        type: ['number', 'array'],
        items: {
            type: 'number',
            minimum: 1,
            maximum: 20,
        },
        default: 2,
    },
    linkColorValues: {
        description: 'Specify the array of values used for dynamic link coloring.',
        type: 'array',
        items: {
            type: ['string', 'number'],
        },
        default: '> primary | seriesByType("number")',
    },
    showDirection: {
        default: 'bidirection',
        description:
            'Specify the directionality of the links. Choose between bidirection, oneWay, none. z and s linkStyles look better with bidirection instead of oneWay.',
        type: 'string',
        pattern: getPattern(['bidirection', 'oneWay', 'none']),
    },
    layout: {
        default: 'grid',
        description: 'Specify the layout used for the graph. Choose between preset, grid, hierarchical',
        type: 'string',
        pattern: getPattern(['preset', 'grid', 'hierarchical', 'force']),
    },
    nodeHorizontalPadding: {
        default: 0,
        description: 'Specify the additional horizontal padding between nodes in pixels (0 - 500).',
        type: 'number',
    },
    nodeVerticalPadding: {
        default: 0,
        description: 'Specify the additional vertical padding between nodes in pixels (0 - 500).',
        type: 'number',
    },
    gridColumns: {
        description:
            'When layout is "grid", specify the number of nodes to display per row. By default it uses the ceiling of the square root of the total number of nodes',
        type: 'number',
    },
    hierarchyDirection: {
        description:
            'When layout is "hierarchical", specify the direction (topBottom, bottomTop, leftRight, rightLeft) of the hierarchy.',
        default: 'leftRight',
        pattern: getPattern(['topBottom', 'bottomTop', 'leftRight', 'rightLeft']),
    },
    chargeStrength: {
        default: -30,
        description:
            'When layout is "force", specify the strength of the charge between nodes. Positive value is attraction and negative value is replusion.',
        type: 'number',
    },
    chargeTheta: {
        default: 0.9,
        description:
            'When layout is "force", specify the Barnes-Hut approximation parameter. Higher value means faster performance but less predictable node positioning.',
        type: 'number',
    },
    chargeDistanceMax: {
        description:
            'When layout is "force", specify the maximum distance over which to apply the charge force.',
        type: 'number',
    },
    chargeDistanceMin: {
        default: 1,
        description:
            'When layout is "force", specify the minimum distance over which to apply the charge force.',
        type: 'number',
    },
    collidePadding: {
        default: 10,
        description:
            'When layout is "force", specify the additional distance around each node which the collider uses for node repulsion. When clustering is on, this value is used to increase collision distance for clusters.',
        type: 'number',
    },
    collideStrength: {
        default: 1,
        description:
            'When layout is "force", specify the strength of the collider used for node repulsion. A higher value results in stronger repulsion, causing nodes to spread out more. Ideally value should be between 0 - 1 but there is no hard limit.',
        type: 'number',
    },
    clusterStrength: {
        minimum: 0,
        maximum: 1,
        default: 0.05,
        description:
            'When layout is "force", specify the strength of the clustering force between nodes of the same type. A higher value results in stronger clustering, causing nodes of the same type to be positioned closer together. Value is between 0 and 1',
        type: 'number',
    },
    linkDistance: {
        default: 30,
        description:
            'When layout is "force", specify the ideal distance between linked nodes. A higher value results in more space between linked nodes.',
        type: 'number',
    },
    linkStrength: {
        minimum: 0,
        maximum: 1,
        description:
            'When layout is "force", specify the strength of the spring force between linked nodes. Higher value makes link lengths closer to the linkDistance value.',
    },
    tooltipNodeFields: {
        description:
            'Specify the fields to add to the tooltips. Tooltips appear when you hover over nodes. These fields and their corresponding values display for each node in its tooltip.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    tooltipHeaderField: {
        description:
            'Specify the field to add to the tooltip header. Tooltips appear when you hover over nodes. Only the value of this field for the node displays as the header.',
        type: 'string',
    },
    xPositions: {
        description: 'When layout is "preset", specify the x position for each node.',
        default: '> primary | frameBySeriesTypes("number") | seriesByIndex(0)',
        type: 'array',
        items: {
            type: 'number',
        },
    },
    yPositions: {
        description: 'When layout is "preset", specify the y position for each node.',
        default: '> primary | frameBySeriesTypes("number") | seriesByIndex(1)',
        type: 'array',
        items: {
            type: 'number',
        },
    },
    nodeDragPositions: {
        description:
            'Stores the positions for nodes by id when the user drags nodes to new positions using the UI.',
        type: 'object',
    },
    preferPorts: {
        description: 'Specify the preferred port alignment for s and z links (horizontal, vertical, none).',
        type: 'string',
        pattern: getPattern(['horizontal', 'vertical', 'none']),
    },
    showZoomControls: {
        default: true,
        description: 'Specify whether to show the zoom controls on the graph.',
        type: 'boolean',
    },
    resultLimit: {
        default: 1000,
        description: 'Specify the maximum number of link data points rendered (hard limit 10,000).',
        type: 'number',
    },
});
